import { inject, Injectable } from '@angular/core';
import { AddressType, AppReviewEvent, CONFIG_TOKEN, FeatureActivityIdentifier } from '@pxl/models/common';
import { LoginService, PxlApplicationStateService, UserService } from '@pxl/models/contracts';
import {
    AddressModel,
    FeaturesAndMetaDataModel,
    FeatureScreenInformationModel,
    FeaturesEffectiveModel,
    FeaturesModel,
    ImageModel,
    ImageUploadModel,
    PostUserSpecificPlatformDataModel,
    UserModel,
    UserService as UserBackendService,
    UserSettingsModel,
    UserSpecificPlatformDataModel,
    UserUpdateModel,
    VeterinarianModel,
} from '@pxl/shared/generated';
import { Convert } from '@pxl/shared/util';
import { FormFieldConfigs, ModelDataValidator } from '@pxl/validation';
import { BehaviorSubject, firstValueFrom, Observable, Subject } from 'rxjs';
import { tap } from 'rxjs/operators';

export function toUserUpdateModel(user: UserModel): UserUpdateModel {
    return {
        title: user.title,
        salutationId: user.salutationId,
        firstName: user.firstName,
        lastName: user.lastName,
        phoneNumber: user.phoneNumber,
        mobileNumber: user.mobileNumber,
        faxNumber: user.faxNumber,
        website: user.website,
        culture: user.culture,
        dateOfBirth: user.dateOfBirth ? Convert.convertBirthDate2UTC(user.dateOfBirth)?.toISOString() : null,
        darkTheme: user.darkTheme,
        floatingChatBotButton: user.floatingChatBotButton,
    };
}

@Injectable()
export class DefaultUserService implements UserService {
    public readonly addressChanged$ = new BehaviorSubject<AddressModel>(null);

    private readonly onUserUpdate$ = new BehaviorSubject<UserModel>(null);
    private readonly userAvatar$ = new BehaviorSubject<ImageModel>(null);
    private readonly userBackground$ = new BehaviorSubject<ImageModel>(null);
    private readonly featureSettingsChange$ = new BehaviorSubject<FeaturesModel>(null);
    private readonly notificationSettingsChanged$ = new Subject<UserSettingsModel>();

    private readonly loginService = inject(LoginService);
    private readonly userBackendService = inject(UserBackendService);
    private readonly applicationStateService = inject(PxlApplicationStateService);
    private readonly config = inject(CONFIG_TOKEN);

    public onAddressChanged(): Observable<AddressModel> {
        return this.addressChanged$.asObservable();
    }

    public onUserAvatar(): Observable<ImageModel> {
        return this.userAvatar$;
    }

    public onUserBackground(): Observable<ImageModel> {
        return this.userBackground$;
    }

    public setUserAvatar(imageModel: ImageModel) {
        this.userAvatar$.next(imageModel);
    }

    public setUserBackground(imageModel: ImageModel): void {
        this.userBackground$.next(imageModel);
    }

    public onUserUpdate(): Observable<UserModel> {
        return this.onUserUpdate$.asObservable();
    }

    public onNotificationSettingsChanged(): Observable<UserSettingsModel> {
        return this.notificationSettingsChanged$.asObservable();
    }

    public getUser(): Observable<UserModel> {
        return this.userBackendService.retrieveUser(this.config.API_VERSION, this.loginService.getToken()).pipe(
            tap((user) => {
                user.dateOfBirth = Convert.convertUTC2LocalBirthDate(user.dateOfBirth)?.toISOString();
                this.userAvatar$.next(user.avatar);
                this.userBackground$.next(user.background);
                this.onUserUpdate$.next(user);
            }),
        );
    }

    public getAddress(): Observable<AddressModel> {
        return this.userBackendService.getAddress(this.config.API_VERSION, this.loginService.getToken());
    }

    public async getFeatureScreenInformation(): Promise<FeatureScreenInformationModel> {
        const { clientId, platformId, appVersion } = await this.loginService.getClientInfos();

        return firstValueFrom(
            this.userBackendService.getFeatureScreenSetSeen(
                clientId,
                platformId as string,
                appVersion,
                this.config.API_VERSION,
                this.loginService.getToken(),
            ),
        );
    }

    public async markFeatureScreenSetSeen(): Promise<void> {
        const { clientId, platformId, appVersion } = await this.loginService.getClientInfos();

        return firstValueFrom(
            this.userBackendService.markFeatureScreenSetSeen(
                clientId,
                platformId as string,
                appVersion,
                this.config.API_VERSION,
                this.loginService.getToken(),
            ),
        );
    }

    public updateUser(user: UserModel): Observable<UserModel> {
        return this.updateUserData(user).pipe(tap(() => this.onUserUpdate$.next(user)));
    }

    public async saveAvatar(viewPort: ImageUploadModel) {
        const imageUploadResultModel = await firstValueFrom(
            this.userBackendService.updateAvatarImage(viewPort, this.config.API_VERSION, this.loginService.getToken()),
        );
        this.setUserAvatar(imageUploadResultModel);

        // notify for AppReview
        this.applicationStateService.raiseEventSpecific(
            new AppReviewEvent({ featureActivityIdentifier: FeatureActivityIdentifier.upload_user_avatar }),
        );
    }

    public async saveBackground(viewPort: ImageUploadModel): Promise<void> {
        const imageUploadResultModel = await firstValueFrom(
            this.userBackendService.updateBackgroundImage(viewPort, this.config.API_VERSION, this.loginService.getToken()),
        );
        this.setUserBackground(imageUploadResultModel);

        // notify for AppReview
        this.applicationStateService.raiseEventSpecific(
            new AppReviewEvent({ featureActivityIdentifier: FeatureActivityIdentifier.upload_user_background }),
        );
    }

    public async deleteProfilePicture() {
        await firstValueFrom(this.userBackendService.removeAvatar(this.config.API_VERSION, this.loginService.getToken()));
        this.userAvatar$.next(null);
    }

    public async deleteBackground() {
        await firstValueFrom(this.userBackendService.removeBackground(this.config.API_VERSION, this.loginService.getToken()));
        this.userBackground$.next(null);
    }

    public getFeatureSettings(): Observable<FeaturesAndMetaDataModel> {
        return this.userBackendService.getFeatures(this.config.API_VERSION, this.loginService.getToken());
    }

    public getEffectiveFeatureSettingsForUserAndVeterinarian(
        filterByVeterinarianIds?: string[],
        filterByFeatureIds?: string[],
    ): Promise<FeaturesEffectiveModel> {
        return firstValueFrom(
            this.userBackendService.getEffectiveFeatures(
                filterByVeterinarianIds,
                filterByFeatureIds,
                this.config.API_VERSION,
                this.loginService.getToken(),
            ),
        );
    }

    public updateFeatureSettings(featuresModel: FeaturesModel): Observable<FeaturesModel> {
        return this.userBackendService
            .updateFeatures(featuresModel, this.config.API_VERSION, this.loginService.getToken())
            .pipe(tap((features) => this.featureSettingsChange$.next(features)));
    }

    public onFeatureSettingsChanged(): Observable<FeaturesModel> {
        return this.featureSettingsChange$;
    }

    public updateAddress(address: AddressModel): Observable<AddressModel> {
        const isCompany = address.addressTypeId === AddressType.Company;
        const newAddress: AddressModel = {
            addressTypeId: address.addressTypeId,
            recipient1: (isCompany && address.recipient1) || null,
            recipient2: (isCompany && address.recipient2) || null,
            recipient3: (isCompany && address.recipient3) || null,
            streetHouseNumber: address.streetHouseNumber,
            postalCode: address.postalCode,
            city: address.city,
            state: address.state,
            countryId: address.countryId,
        };

        return this.userBackendService
            .updateAddress(newAddress, this.config.API_VERSION, this.loginService.getToken())
            .pipe(tap(() => this.addressChanged$.next(newAddress)));
    }

    public getUserSettings(): Promise<UserSettingsModel> {
        return firstValueFrom(this.userBackendService.getSettings(this.config.API_VERSION, this.loginService.getToken()));
    }

    public saveUserSettings(userSettings: UserSettingsModel): Observable<null> {
        return this.userBackendService
            .updateSettings(userSettings, this.config.API_VERSION, this.loginService.getToken())
            .pipe(tap(() => this.notificationSettingsChanged$.next(userSettings)));
    }

    public validateRequiredUserFields(user: UserModel): boolean {
        const formFieldConfig = FormFieldConfigs.CommonPersonalDataConstraints;
        const userModelValidator = new ModelDataValidator<UserModel, typeof formFieldConfig>(formFieldConfig, [
            { sourceKey: 'salutationId', configKey: 'salutation' },
        ]);
        return userModelValidator.validateModel(user);
    }

    public validateRequiredAddressFields(addressModel: AddressModel): boolean {
        if (addressModel) {
            const { streetHouseNumber, city, postalCode, countryId } = addressModel;
            return !!(streetHouseNumber && city && postalCode && countryId);
        }
        return false;
    }

    public addVeterinarian(veterinarianId: VeterinarianModel['id']): Observable<null> {
        return this.userBackendService.addVeterinarian(veterinarianId, this.config.API_VERSION, this.loginService.getToken());
    }

    public removeVeterinarian(veterinarianId: VeterinarianModel['id']): Observable<null> {
        return this.userBackendService.removeVeterinarian(veterinarianId, this.config.API_VERSION, this.loginService.getToken());
    }

    public getUserSpecificPlatformData(): Observable<UserSpecificPlatformDataModel[]> {
        return this.userBackendService.getUserSpecificPlatformData(this.config.API_VERSION, this.loginService.getToken());
    }

    public storeUserSpecificPlatformData(
        userSpecificPlatformDataModel: PostUserSpecificPlatformDataModel,
    ): Observable<UserSpecificPlatformDataModel> {
        return this.userBackendService.storeUserSpecificPlatformData(
            userSpecificPlatformDataModel,
            this.config.API_VERSION,
            this.loginService.getToken(),
        );
    }

    public deleteUserSpecificPlatformData(userSpecificPlatformDataModelId: string): Observable<void> {
        return this.userBackendService.deleteUserSpecificPlatformData(
            userSpecificPlatformDataModelId,
            this.config.API_VERSION,
            this.loginService.getToken(),
        );
    }

    private updateUserData(user: UserModel): Observable<UserModel> {
        return this.userBackendService.updateUser(toUserUpdateModel(user), this.config.API_VERSION, this.loginService.getToken());
    }
}
